import { useTranslation } from "react-i18next";
import { Braces, CircleAlert, CircleCheck, Pencil } from "lucide-react";
import type { CampaignVariableMapping } from "@multi-chat/shared";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import {
  mappingEntryLabel,
  sourceTypeLabelKey,
  variableTokenLabel,
} from "./campaign-message-presentation.js";

function VariableRow({
  parameterKey,
  mapping,
  canMutate,
  onMap,
}: {
  parameterKey: string;
  mapping: CampaignVariableMapping;
  canMutate: boolean;
  onMap: (parameterKey: string, tokenId: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const entry = mapping.variables[parameterKey] ?? null;
  const token = variableTokenLabel(parameterKey);
  const tokenId = `variables-${parameterKey}`;
  const content = (
    <>
      <LtrValue className="shrink-0 rounded-md border border-line/70 bg-surface-muted px-1.5 py-0.5 font-mono text-[0.7rem] font-semibold text-ink">
        {token}
      </LtrValue>
      {entry ? (
        <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[0.75rem]">
          <CircleCheck size={13} className="shrink-0 text-success" aria-hidden="true" />
          <span className="min-w-0 truncate text-ink" dir="auto">
            <span className="text-muted">{t(sourceTypeLabelKey(entry.source))} · </span>
            {mappingEntryLabel(t, entry)}
          </span>
        </span>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[0.75rem] font-medium text-warning">
          <CircleAlert size={13} className="shrink-0" aria-hidden="true" />
          <span className="min-w-0 truncate">{t("campaigns.message.unmapped")}</span>
        </span>
      )}
      {canMutate ? (
        <Pencil
          size={13}
          className="shrink-0 text-subtle transition-colors group-hover:text-ink"
          aria-hidden="true"
        />
      ) : null}
    </>
  );
  const rowClass = cn(
    "flex h-9 w-full min-w-0 items-center gap-2.5 rounded-lg border px-2.5 text-start",
    entry ? "border-line/70 bg-surface" : "border-line/70 border-s-2 border-s-warning bg-surface",
  );

  return (
    <li className="min-w-0" data-testid={`campaign-template-variable-${parameterKey}`}>
      {canMutate ? (
        <button
          type="button"
          className={cn(
            rowClass,
            "group transition-[border-color,background-color] hover:border-line-strong hover:bg-surface-muted/60 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-ring)]",
          )}
          data-token-id={tokenId}
          aria-label={t("campaigns.content.configureVariableNamed", { token })}
          onClick={() => onMap(parameterKey, tokenId)}
        >
          {content}
        </button>
      ) : (
        <div className={rowClass}>{content}</div>
      )}
    </li>
  );
}

/**
 * Template variables summary plus a clickable row per required variable. Rows call
 * the same `onMap` handler as the phone tokens, so they open the same mapping dialog.
 */
export function CampaignTemplateVariablesSection({
  requiredKeys,
  unrenderedKeys,
  mappedCount,
  mapping,
  canMutate,
  onMap,
}: {
  requiredKeys: readonly string[];
  /** Required keys with no clickable token in the phone preview. */
  unrenderedKeys: readonly string[];
  mappedCount: number;
  mapping: CampaignVariableMapping;
  canMutate: boolean;
  onMap: (parameterKey: string, tokenId: string) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (requiredKeys.length === 0) return null;

  const unrendered = new Set(unrenderedKeys);
  const previewKeys = requiredKeys.filter((key) => !unrendered.has(key));
  const additionalKeys = requiredKeys.filter((key) => unrendered.has(key));
  const allConfigured = mappedCount === requiredKeys.length;
  const percent = Math.round((mappedCount / requiredKeys.length) * 100);

  return (
    <section
      className="grid min-w-0 gap-4 border-t border-line/60 pt-5"
      aria-labelledby="campaign-template-variables-title"
      data-testid="campaign-content-variable-readiness"
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <span
          className="inline-grid size-8 shrink-0 place-items-center rounded-lg border border-line/70 bg-surface-muted text-ink"
          aria-hidden="true"
        >
          <Braces size={15} />
        </span>
        <div className="grid min-w-0 gap-0.5">
          <h4 id="campaign-template-variables-title" className="m-0 text-[0.8125rem] font-semibold">
            {t("campaigns.content.templateVariablesTitle")}
          </h4>
          <p className="m-0 text-[0.75rem] leading-snug text-muted">
            {t("campaigns.content.templateVariablesDescription")}
          </p>
        </div>
      </div>

      <div className="grid w-full max-w-[24rem] gap-1.5">
        <p
          className={cn(
            "m-0 text-[0.75rem] font-medium tabular-nums",
            allConfigured ? "text-success" : "text-ink",
          )}
        >
          {t("campaigns.content.variablesConfigured", {
            configured: mappedCount,
            total: requiredKeys.length,
          })}
        </p>
        <div
          className="flex h-1.5 overflow-hidden rounded-full bg-surface-muted"
          role="progressbar"
          aria-labelledby="campaign-template-variables-title"
          aria-valuemin={0}
          aria-valuemax={requiredKeys.length}
          aria-valuenow={mappedCount}
          data-testid="campaign-template-variables-progress"
        >
          <div
            className="h-full rounded-full bg-accent transition-[width] duration-300 ease-out"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      {previewKeys.length > 0 ? (
        <ul className="m-0 grid list-none gap-2 p-0">
          {previewKeys.map((key) => (
            <VariableRow
              key={key}
              parameterKey={key}
              mapping={mapping}
              canMutate={canMutate}
              onMap={onMap}
            />
          ))}
        </ul>
      ) : null}

      {additionalKeys.length > 0 ? (
        <div className="grid gap-1.5" data-testid="campaign-personalization">
          <h5 className="m-0 text-[0.7rem] font-semibold text-muted">
            {t("campaigns.content.additionalVariables")}
          </h5>
          <ul className="m-0 grid list-none gap-2 p-0">
            {additionalKeys.map((key) => (
              <VariableRow
                key={key}
                parameterKey={key}
                mapping={mapping}
                canMutate={canMutate}
                onMap={onMap}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {!allConfigured && canMutate ? (
        <p className="m-0 text-[0.7rem] leading-snug text-subtle">
          {t("campaigns.message.clickVariableHint")}
        </p>
      ) : null}
    </section>
  );
}
